import { formatCents } from '@/lib/format/currency';
import { saoPauloDayKey } from './statementPeriods';
import type { WalletStatementLine } from './statementTypes';

export type StatementLineCopy = { key: string; values?: Record<string, string> };
export type StatementLineDescription = {
  title: StatementLineCopy;
  detail: StatementLineCopy | null;
  tone: 'credit' | 'debit';
};

const RELEASE_SOURCES = new Set(['payment', 'course', 'cpm']);
const PAYOUT_KINDS = new Set(['pix_withdrawal', 'plan_redemption', 'minutes_redemption']);
const REVERSAL_REASONS = new Set(['recalculated', 'forfeited', 'refunded', 'chargeback', 'other']);

const payoutKindKey = (kind: string | null) =>
  `walletStatement.lines.payout.${kind && PAYOUT_KINDS.has(kind) ? kind : 'pix_withdrawal'}`;

export function formatSignedCents(cents: number): string {
  return `${cents < 0 ? '−' : '+'}${formatCents(Math.abs(cents), 'BRL')}`;
}

export function describeStatementLine(
  line: WalletStatementLine,
  { today, formatDay }: { today: string; formatDay: (day: string) => string },
): StatementLineDescription {
  const tone = line.amount_cents < 0 ? 'debit' : 'credit';
  const producer = line.earning_kind === 'producer';

  switch (line.kind) {
    case 'ranking_award':
      return { title: { key: 'ranking.walletAward' }, detail: null, tone };
    case 'cpm_daily': {
      if (line.since_day) {
        return { title: { key: 'walletStatement.lines.cpmSince', values: { date: formatDay(line.since_day) } }, detail: null, tone };
      }
      const isToday = line.id === `cpm:${today}`;
      return { title: { key: isToday ? 'walletStatement.lines.cpmToday' : 'walletStatement.lines.cpmDaily' }, detail: null, tone };
    }
    case 'cpm_adjustment':
      return { title: { key: 'walletStatement.lines.cpmAdjustment' }, detail: null, tone };
    case 'commission_release':
      if (producer) {
        return {
          title: { key: 'walletStatement.lines.royaltyRelease' },
          detail: line.course_title ? { key: 'walletStatement.lines.course', values: { course: line.course_title } } : null,
          tone,
        };
      }
      return {
        title: { key: `walletStatement.lines.commissionRelease.${line.source && RELEASE_SOURCES.has(line.source) ? line.source : 'payment'}` },
        detail: line.referred_user_name ? { key: 'walletStatement.lines.fromReferred', values: { name: line.referred_user_name } } : null,
        tone,
      };
    case 'commission_reversal':
      return {
        title: { key: producer ? 'walletStatement.lines.royaltyReversal' : 'walletStatement.lines.commissionReversal' },
        detail: { key: `walletStatement.reversalReason.${line.reversal_reason && REVERSAL_REASONS.has(line.reversal_reason) ? line.reversal_reason : 'other'}` },
        tone,
      };
    case 'payout': {
      let detail: StatementLineCopy = { key: 'walletStatement.payoutStatus.paid' };
      if (line.payout_status === 'pending') detail = { key: 'walletStatement.payoutStatus.pending' };
      else if (line.payout_status === 'cancelled') detail = { key: 'walletStatement.payoutStatus.cancelled' };
      else if (line.paid_at) {
        detail = { key: 'walletStatement.payoutStatus.paidOn', values: { date: formatDay(saoPauloDayKey(new Date(line.paid_at))) } };
      }
      return { title: { key: payoutKindKey(line.payout_kind) }, detail, tone };
    }
    case 'payout_cancellation':
      return { title: { key: 'walletStatement.lines.payoutCancellation' }, detail: { key: payoutKindKey(line.payout_kind) }, tone };
    case 'adjustment':
      return { title: { key: 'walletStatement.lines.adjustment' }, detail: { key: 'walletStatement.lines.adjustmentHint' }, tone };
    default:
      return { title: { key: 'walletStatement.lines.unknown' }, detail: null, tone };
  }
}
